// src/pages/ResetPassword.jsx
import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box, Button, TextField, Typography, Stack, Alert,
  CircularProgress, Paper, InputAdornment, IconButton, LinearProgress,
} from "@mui/material";
import AutoStoriesIcon   from "@mui/icons-material/AutoStories";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import API from "../api";
import { indigoGradient, C } from "../theme";

function pwdStrength(pwd) {
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score; // 0-5
}

const STRENGTH_LABELS = ["", "Très faible", "Faible", "Moyen", "Fort", "Très fort"];
const STRENGTH_COLORS = ["", "#ef4444", "#f97316", "#eab308", "#22c55e", "#16a34a"];

export default function ResetPassword() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const token     = new URLSearchParams(location.search).get("token") || "";

  const [pwd,     setPwd]     = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  const strength    = pwdStrength(pwd);
  const strengthPct = (strength / 5) * 100;
  const match       = pwd && confirm && pwd === confirm;
  const mismatch    = confirm && pwd !== confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!match) { setError("Les mots de passe ne correspondent pas."); return; }
    if (pwd.length < 8) { setError("Le mot de passe doit faire au moins 8 caractères."); return; }
    setError(""); setLoading(true);
    try {
      await API.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lien invalide ou expiré. Refaites une demande.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) return (
    <Box sx={{ minHeight:"78vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <Alert severity="error" sx={{ maxWidth:400, borderRadius:"10px" }}>
        Lien invalide. <Link to="/forgot-password">Refaites une demande.</Link>
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ minHeight:"78vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <Box sx={{ width:"100%", maxWidth:400 }}>
        <Box sx={{ textAlign:"center", mb:4 }}>
          <Box sx={{ width:54, height:54, borderRadius:3, mx:"auto", mb:2,
            background:indigoGradient, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <AutoStoriesIcon sx={{ color:"white", fontSize:26 }} />
          </Box>
          <Typography variant="h4" fontWeight={700}>Nouveau mot de passe</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt:.5 }}>
            Choisissez un mot de passe sécurisé
          </Typography>
        </Box>

        <Paper sx={{ p:4, borderRadius:"16px" }}>
          {done ? (
            <Stack spacing={2.5} alignItems="center">
              <Box sx={{ width:56, height:56, borderRadius:"50%", background:"#dcfce7",
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <CheckCircleIcon sx={{ color:"#16a34a", fontSize:28 }} />
              </Box>
              <Alert severity="success" sx={{ width:"100%", borderRadius:"8px" }}>
                Mot de passe modifié avec succès !
              </Alert>
              <Button variant="contained" fullWidth onClick={() => navigate("/login")}
                sx={{ borderRadius:"8px", textTransform:"none", py:1.3 }}>
                Se connecter
              </Button>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {error && <Alert severity="error" sx={{ borderRadius:"8px" }}>{error}</Alert>}

                {/* Nouveau mot de passe */}
                <Box>
                  <TextField
                    label="Nouveau mot de passe" fullWidth required autoFocus
                    type={showPwd ? "text" : "password"}
                    value={pwd} onChange={e => setPwd(e.target.value)}
                    autoComplete="new-password"
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPwd(v => !v)} edge="end">
                          {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )}}
                  />
                  {/* Indicateur de force */}
                  {pwd && (
                    <Box sx={{ mt:1 }}>
                      <LinearProgress variant="determinate" value={strengthPct}
                        sx={{ height:4, borderRadius:2,
                          "& .MuiLinearProgress-bar": { background: STRENGTH_COLORS[strength] } }} />
                      <Typography variant="caption"
                        sx={{ color: STRENGTH_COLORS[strength], fontWeight:600, mt:.5, display:"block" }}>
                        {STRENGTH_LABELS[strength]}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Confirmation */}
                <TextField
                  label="Confirmer le mot de passe" fullWidth required
                  type="password"
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  error={mismatch}
                  helperText={mismatch ? "Les mots de passe ne correspondent pas" : match ? "✓ Identiques" : ""}
                  FormHelperTextProps={{ sx: { color: match ? "#16a34a" : undefined } }}
                />

                <Button type="submit" variant="contained" size="large" fullWidth
                  disabled={loading || !pwd || !confirm}
                  sx={{ py:1.3, mt:.5, borderRadius:"8px" }}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Réinitialiser le mot de passe"}
                </Button>

                <Typography variant="body2" sx={{ textAlign:"center", color:"text.secondary" }}>
                  <Link to="/login"
                    style={{ color:C.indigo[600], fontWeight:600, textDecoration:"none" }}>
                    ← Retour à la connexion
                  </Link>
                </Typography>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
